import { useRef, useState, useCallback } from "react";
import { MoveHorizontal } from "lucide-react";
import { responsive } from "../lib/img";

// Draggable before/after comparison slider.
export default function BeforeAfterSlider({ before, after, beforeLabel = "BEFORE", afterLabel = "SPEEDY RESURFACING", height = "h-[420px] md:h-[560px]", testid = "before-after" }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const move = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(2, Math.min(98, pct)));
  }, []);

  const start = () => (dragging.current = true);
  const stop = () => (dragging.current = false);
  const onMouseMove = (e) => { if (dragging.current) move(e.clientX); };
  const onTouchMove = (e) => move(e.touches[0].clientX);

  return (
    <div
      ref={ref}
      data-testid={testid}
      className={`relative w-full ${height} overflow-hidden rounded-sm select-none bg-obsidian-800 cursor-ew-resize`}
      onMouseMove={onMouseMove}
      onMouseDown={(e) => { start(); move(e.clientX); }}
      onMouseUp={stop}
      onMouseLeave={stop}
      onTouchStart={start}
      onTouchMove={onTouchMove}
      onTouchEnd={stop}
    >
      <img {...responsive(after, "(min-width: 1024px) 640px, 100vw", [480, 800, 1200])} decoding="async" alt="Finished court after resurfacing" className="absolute inset-0 h-full w-full object-cover" draggable={false} />
      <span className="absolute top-4 right-4 z-10 rounded-sm bg-neon px-3 py-1 font-mono text-[10px] font-bold tracking-widest text-obsidian">{afterLabel}</span>

      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img {...responsive(before, "(min-width: 1024px) 640px, 100vw", [480, 800, 1200])} decoding="async" alt="Court before resurfacing" className="absolute inset-0 h-full w-full object-cover"
             style={{ width: ref.current ? ref.current.getBoundingClientRect().width : "100%", maxWidth: "none" }} draggable={false} />
        <div className="absolute inset-0 bg-black/40" />
        <span className="absolute top-4 left-4 rounded-sm border border-white/30 bg-black/60 px-3 py-1 font-mono text-[10px] font-bold tracking-widest text-white">{beforeLabel}</span>
      </div>

      <div className="absolute top-0 bottom-0 z-20" style={{ left: `calc(${pos}% - 1px)` }}>
        <div className="h-full w-0.5 bg-neon shadow-neon" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-neon text-obsidian shadow-neon">
          <MoveHorizontal size={20} />
        </div>
      </div>
    </div>
  );
}
